import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useForm, Controller } from 'react-hook-form';
import { useAllProgramVersionsDetailed } from '../../api/hooks/program.hooks';
import { useGoals, useCreateGoal } from '../../api/hooks/useGoals';
import { useCreateChallenge, useChallenges, useAddOrganizationsToChallenge } from '../../api/hooks/useChallenges';
import { GoalDto } from '../../api/types/dtos/common';
import { ProgramVersionDto } from '../../api/types/dtos/program';
import { CreateChallengeDto } from '../../api/types/dtos/challenge/ChallengeDto';
import { ChallengeDto } from '../../api/types/dtos/challenge';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useOrganizationsListPaginated } from '../../api/hooks/useOrganizations';
import { OrganizationListDto, DateField, DateCondition } from '../../api/services/organization.service';
import { useTablePagination, useSearchWithPagination } from '../../api/hooks/useTablePagination';
import { fetchOrganizationsListPaginated } from '../../api/services/organization.service';

interface CreateChallengeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  name: string;
  description: string;
  programVersions: ProgramVersionDto[];
  goal: GoalDto | null;
}

const CreateChallengeModal: React.FC<CreateChallengeModalProps> = ({ open, onClose, onSuccess }) => {
  const { control, handleSubmit, reset, } = useForm<FormValues>({
    defaultValues: {
      name: '',
      description: '',
      programVersions: [],
      goal: null,
    },
  });

  const { selectedOrganization } = useOrganization(); // Owner organization from context
  const { data: programVersions, isLoading: pvLoading } = useAllProgramVersionsDetailed(selectedOrganization?.id || '');
  const { data: goals, isLoading: goalsLoading } = useGoals(selectedOrganization?.id || '');
  const createGoalMutation = useCreateGoal(selectedOrganization?.id || '');
  const createChallengeMutation = useCreateChallenge();
  const addOrgsMutation = useAddOrganizationsToChallenge();
  const { data: existingChallengesData } = useChallenges();

  const distinctExistingChallenges = useMemo(() => {
    const all = existingChallengesData?.data || [];
    const seen = new Set<string>();
    return all.filter((c) => {
      if (!c.name || seen.has(c.name)) return false;
      seen.add(c.name);
      return true;
    });
  }, [existingChallengesData]);

  const [challengeMode, setChallengeMode] = useState<'new' | 'existing'>('new');
  const [selectedExistingChallenge, setSelectedExistingChallenge] = useState<ChallengeDto | null>(null);
  const [newGoalName, setNewGoalName] = useState('');
  const [newGoalDesc, setNewGoalDesc] = useState('');
  const [showGoalInput, setShowGoalInput] = useState(false);

  const [step, setStep] = useState(1);
  const [selectedOrgsForSubmission, setSelectedOrgsForSubmission] = useState<OrganizationListDto[]>([]);
  const [selectingAllMatching, setSelectingAllMatching] = useState(false);

  // Date filter state
  const [dateField, setDateField] = useState<DateField>('createdAt');
  const [dateCondition, setDateCondition] = useState<DateCondition>('after');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Pagination state
  const {
    page,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    resetPagination,
  } = useTablePagination({
    initialPage: 0,
    initialPageSize: 25,
  });

  // Search state with debouncing
  const {
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm,
    clearSearch,
  } = useSearchWithPagination(1000, resetPagination);

  // Use paginated hook with search
  const {
    data: orgListData,
    isLoading: orgListLoading,
    error: _orgListError,
  } = useOrganizationsListPaginated(
    selectedOrganization?.id || 'placeholder',
    {
      page,
      size: pageSize,
      searchTerm: debouncedSearchTerm || undefined,
      dateField: dateFrom ? dateField : undefined,
      dateCondition: dateFrom ? dateCondition : undefined,
      dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
      dateTo: dateTo && dateCondition === 'between' ? new Date(dateTo).toISOString() : undefined,
    },
    {
      enabled: !!selectedOrganization?.id
    }
  );

  // Reset state when modal is opened
  useEffect(() => {
    if (open) {
      setStep(1);
      setChallengeMode('new');
      setSelectedExistingChallenge(null);
      setSelectedOrgsForSubmission([]);
      clearSearch();
      resetPagination();
      setDateField('createdAt');
      setDateCondition('after');
      setDateFrom('');
      setDateTo('');
      reset({ name: '', description: '', programVersions: [], goal: null });
    } else {
      setStep(1); 
      setChallengeMode('new');
      setSelectedExistingChallenge(null);
      setSelectedOrgsForSubmission([]);
      clearSearch();
      resetPagination();
      setDateField('createdAt');
      setDateCondition('after');
      setDateFrom('');
      setDateTo('');
    }
  }, [open, reset, clearSearch, resetPagination]);

  // Get current page organizations - ensure it's always an array
  // Data structure: orgListData.data.content contains the array
  const currentPageOrgs = useMemo(() => {
    if (!orgListData || !orgListData.data) return [];
    const content = orgListData.data.content;
    return Array.isArray(content) ? content : [];
  }, [orgListData]);

  // Check if all current page orgs are selected
  const allCurrentPageSelected = useMemo(() => {
    if (!Array.isArray(currentPageOrgs) || currentPageOrgs.length === 0) return false;
    return currentPageOrgs.every(org => selectedOrgsForSubmission.some(sel => sel.id === org.id));
  }, [currentPageOrgs, selectedOrgsForSubmission]);

  // Check if some (but not all) current page orgs are selected
  const someCurrentPageSelected = useMemo(() => {
    if (!Array.isArray(currentPageOrgs) || currentPageOrgs.length === 0) return false;
    return currentPageOrgs.some(org => selectedOrgsForSubmission.some(sel => sel.id === org.id)) && !allCurrentPageSelected;
  }, [currentPageOrgs, selectedOrgsForSubmission, allCurrentPageSelected]);

  // Handle select all on current page
  const handleSelectAllCurrentPage = (checked: boolean) => {
    if (!Array.isArray(currentPageOrgs)) return;
    
    if (checked) {
      const newSelected = [...selectedOrgsForSubmission];
      currentPageOrgs.forEach(org => {
        if (!newSelected.some(sel => sel.id === org.id)) {
          newSelected.push(org);
        }
      });
      setSelectedOrgsForSubmission(newSelected);
    } else {
      setSelectedOrgsForSubmission(selectedOrgsForSubmission.filter(sel => !currentPageOrgs.some(org => org.id === sel.id)));
    }
  };

  // Handle select all matching filter (all pages)
  const handleSelectAllMatchingFilter = useCallback(async () => {
    if (!selectedOrganization?.id) return;
    
    setSelectingAllMatching(true);
    try {
      const allOrgs: OrganizationListDto[] = [];
      let currentPage = 0;
      let hasMore = true;
      const pageSizeForFetch = 100; // Use larger page size for fetching all

      while (hasMore) {
        const response = await fetchOrganizationsListPaginated(
          selectedOrganization.id,
          {
            page: currentPage,
            size: pageSizeForFetch,
            searchTerm: debouncedSearchTerm || undefined,
            dateField: dateFrom ? dateField : undefined,
            dateCondition: dateFrom ? dateCondition : undefined,
            dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
            dateTo: dateTo && dateCondition === 'between' ? new Date(dateTo).toISOString() : undefined,
          }
        );

        if (response.data && response.data.content && Array.isArray(response.data.content) && response.data.content.length > 0) {
          allOrgs.push(...response.data.content);
          currentPage++;
          
          // Check if we've reached the last page
          if (response.data.content.length < pageSizeForFetch || currentPage >= response.data.totalPages) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      // Add all fetched orgs to selection (avoid duplicates)
      setSelectedOrgsForSubmission(prev => {
        const newSelected = [...prev];
        allOrgs.forEach(org => {
          if (!newSelected.some(sel => sel.id === org.id)) {
            newSelected.push(org);
          }
        });
        return newSelected;
      });
    } catch (error) {
      console.error('Error selecting all matching organizations:', error);
    } finally {
      setSelectingAllMatching(false);
    }
  }, [selectedOrganization?.id, debouncedSearchTerm, dateField, dateCondition, dateFrom, dateTo]);

  const handleToggleOrg = (org: OrganizationListDto) => {
    setSelectedOrgsForSubmission(prev =>
      prev.some(sel => sel.id === org.id)
        ? prev.filter(sel => sel.id !== org.id)
        : [...prev, org]
    );
  };

  const handleRemoveOrg = (orgId: string) => {
    setSelectedOrgsForSubmission(prev => prev.filter(sel => sel.id !== orgId));
  };

  const handleCreateGoal = async () => {
    if (!newGoalName) return;
    await createGoalMutation.mutateAsync({ name: newGoalName, description: newGoalDesc });
    setNewGoalName('');
    setNewGoalDesc('');
    setShowGoalInput(false);
  };

  const onSubmit = async (values: FormValues) => {

    if (!selectedOrgsForSubmission || selectedOrgsForSubmission.length === 0) {
      console.error('CreateChallengeModal - onSubmit: No organizations selected for submission!');
      return;
    }
    if (!values.programVersions || values.programVersions.length === 0) {
      console.error('CreateChallengeModal - onSubmit: No program versions selected!');
      return;
    }
    if (!selectedOrganization?.id) {
      console.error('CreateChallengeModal - onSubmit: No owner organization selected in context!');
      return;
    }

    const dto: CreateChallengeDto & { organizationId: string } = {
      name: values.name,
      description: values.description,
      inspectedOrgIds: selectedOrgsForSubmission.map(o => o.id),
      programsVersionIds: values.programVersions.map(pv => pv.id),
      goalId: values.goal?.id,
      organizationId: selectedOrganization.id, // Owner organization
    };
    
    
    try {
      await createChallengeMutation.mutateAsync(dto as any);
      
      onSuccess(); // Parent handles closing and further actions
    } catch (error) {
      console.error('CreateChallengeModal - onSubmit: createChallengeMutation failed:', error);
    }
  };

  const handleAddOrgsToExistingChallenge = async () => {
    if (!selectedExistingChallenge) {
      console.error('CreateChallengeModal - handleAddOrgsToExistingChallenge: No existing challenge selected!');
      return;
    }
    if (!selectedOrgsForSubmission || selectedOrgsForSubmission.length === 0) {
      console.error('CreateChallengeModal - handleAddOrgsToExistingChallenge: No organizations selected!');
      return;
    }

    try {
      await addOrgsMutation.mutateAsync({
        challengeId: selectedExistingChallenge.id,
        inspectedOrgIds: selectedOrgsForSubmission.map(o => o.id),
      });
      onSuccess(); // Parent handles closing and further actions
    } catch (error) {
      console.error('CreateChallengeModal - handleAddOrgsToExistingChallenge: failed:', error);
    }
  };

  const handleNextStep = () => {
    if (selectedOrgsForSubmission.length > 0) {
      setStep(2);
    } else {
      // Optionally, show an error/alert that at least one organization must be selected
      console.warn('CreateChallengeModal - Attempted to go to next step without selecting organizations.');
    }
  };

  const handlePreviousStep = () => {
    setStep(1);
  };

  // Handles full modal closure from Cancel buttons
  const handleModalCloseAndReset = () => {
    console.log('CreateChallengeModal - handleModalCloseAndReset called, closing and resetting.');
    onClose(); // Parent will close the modal. useEffect for 'open' will reset internal state.
  };
  
  return (
    <Dialog open={open} onClose={handleModalCloseAndReset} maxWidth="lg" fullWidth>
      <DialogTitle>
        {challengeMode === 'new'
          ? (step === 1 ? 'Selecciona Organizaciones y Crea un Reto' : 'Detalla el Nuevo Reto')
          : (step === 1 ? 'Selecciona Organizaciones' : 'Selecciona el Reto Existente')}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 400 }}>
        {/* Mode Toggle */}
        <ToggleButtonGroup
          value={challengeMode}
          exclusive
          onChange={(_, value) => { if (value) { setChallengeMode(value); setSelectedExistingChallenge(null); } }}
          aria-label="challenge mode"
          sx={{ mb: 2, alignSelf: 'center' }}
          size="small"
        >
          <ToggleButton value="new">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AddCircleIcon fontSize="small" />
              <Typography variant="button">Nuevo Reto</Typography>
            </Box>
          </ToggleButton>
          <ToggleButton value="existing">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AssignmentIcon fontSize="small" />
              <Typography variant="button">Reto Existente</Typography>
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Stepper */}
        <Stepper activeStep={step - 1} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>{`Organizaciones (${selectedOrgsForSubmission.length})`}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{challengeMode === 'new' ? 'Configurar Reto' : 'Seleccionar Reto'}</StepLabel>
          </Step>
        </Stepper>
        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 500 }}>
            {/* Search and selection controls */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                label="Buscar Organización"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                fullWidth
                placeholder="Nombre, ID, grupo, dueño o teléfono"
                size="small"
              />
              {/* Date filter */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Campo de fecha</InputLabel>
                  <Select
                    value={dateField}
                    onChange={e => setDateField(e.target.value as DateField)}
                    label="Campo de fecha"
                  >
                    <MenuItem value="createdAt">Fecha de creación</MenuItem>
                    <MenuItem value="updatedAt">Fecha de modificación</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Condición</InputLabel>
                  <Select
                    value={dateCondition}
                    onChange={e => setDateCondition(e.target.value as DateCondition)}
                    label="Condición"
                  >
                    <MenuItem value="after">Después de</MenuItem>
                    <MenuItem value="before">Antes de</MenuItem>
                    <MenuItem value="on">En</MenuItem>
                    <MenuItem value="between">Entre</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  type="datetime-local"
                  size="small"
                  label={dateCondition === 'between' ? 'Desde' : 'Fecha'}
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                />
                {dateCondition === 'between' && (
                  <TextField
                    type="datetime-local"
                    size="small"
                    label="Hasta"
                    value={dateTo}
                    onChange={e => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                  />
                )}
                {(dateFrom || dateTo) && (
                  <IconButton
                    size="small"
                    onClick={() => { setDateFrom(''); setDateTo(''); }}
                    title="Limpiar filtro de fecha"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allCurrentPageSelected}
                      indeterminate={someCurrentPageSelected}
                      onChange={e => handleSelectAllCurrentPage(e.target.checked)}
                      disabled={orgListLoading || currentPageOrgs.length === 0 || selectingAllMatching}
                    />
                  }
                  label="Seleccionar todos los de esta página"
                  sx={{ whiteSpace: 'nowrap' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={false}
                      onChange={() => handleSelectAllMatchingFilter()}
                      disabled={orgListLoading || selectingAllMatching}
                    />
                  }
                  label={
                    selectingAllMatching ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <span>Seleccionando...</span>
                      </Box>
                    ) : (
                      'Seleccionar todos los de este filtro'
                    )
                  }
                  sx={{ whiteSpace: 'nowrap' }}
                />
              </Box>
            </Box>

            {/* Two-column layout */}
            <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 400 }}>
              {/* Left column: Available organizations */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Organizaciones Disponibles
                  </Typography>
                  {orgListLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <List 
                        sx={{ 
                          maxHeight: 350, 
                          overflow: 'auto', 
                          border: '1px solid #eee', 
                          borderRadius: 1, 
                          flexGrow: 1 
                        }} 
                        disablePadding
                      >
                        {currentPageOrgs.map(org => {
                          const isSelected = selectedOrgsForSubmission.some(sel => sel.id === org.id);
                          return (
                            <ListItem
                              {...({as: 'div'} as any)}
                              key={org.id}
                              onClick={() => handleToggleOrg(org)}
                              button
                              selected={isSelected}
                            >
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={isSelected}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={`${org.name} (${org.idNumber})`}
                                secondary={`Dueño: ${org.owner?.name || 'N/A'} | Tel: ${org.owner?.phones?.join(', ') || 'N/A'} | Grupos: ${org.groupNames?.join(', ') || 'N/A'} | Creado: ${org.createdAt ? new Date(org.createdAt).toLocaleDateString() : 'N/A'} | Modificado: ${org.updatedAt ? new Date(org.updatedAt).toLocaleDateString() : 'N/A'}`}
                              />
                            </ListItem>
                          );
                        })}
                        {currentPageOrgs.length === 0 && !orgListLoading && (
                          <ListItem>
                            <ListItemText primary="No se encontraron organizaciones con los filtros actuales." />
                          </ListItem>
                        )}
                      </List>
                      
                      {/* Pagination controls */}
                      {orgListData && orgListData.data && orgListData.data.totalPages > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Por página</InputLabel>
                              <Select
                                value={pageSize}
                                onChange={(e) => handlePageSizeChange(e.target.value as number)}
                                label="Por página"
                              >
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                              </Select>
                            </FormControl>
                            <Typography variant="body2" color="text.secondary">
                              Mostrando {orgListData.data.page * orgListData.data.size + 1}-
                              {Math.min((orgListData.data.page + 1) * orgListData.data.size, orgListData.data.totalElements)} de{' '}
                              {orgListData.data.totalElements} registros
                            </Typography>
                          </Box>
                          {orgListData.data.totalPages > 1 && (
                            <Pagination
                              count={orgListData.data.totalPages}
                              page={orgListData.data.page + 1}
                              onChange={(_event, value) => handlePageChange(value - 1)}
                              color="primary"
                              size="small"
                              showFirstButton
                              showLastButton
                            />
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Grid>

              {/* Right column: Selected organizations */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Organizaciones Seleccionadas ({selectedOrgsForSubmission.length})
                  </Typography>
                  <List 
                    sx={{ 
                      maxHeight: 350, 
                      overflow: 'auto', 
                      border: '1px solid #eee', 
                      borderRadius: 1, 
                      flexGrow: 1 
                    }} 
                    disablePadding
                  >
                    {selectedOrgsForSubmission.map(org => (
                      <ListItem
                        key={org.id}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="Eliminar"
                            onClick={() => handleRemoveOrg(org.id)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={`${org.name} (${org.idNumber})`}
                          secondary={`Dueño: ${org.owner?.name || 'N/A'} | Tel: ${org.owner?.phones?.join(', ') || 'N/A'} | Grupos: ${org.groupNames?.join(', ') || 'N/A'} | Creado: ${org.createdAt ? new Date(org.createdAt).toLocaleDateString() : 'N/A'} | Modificado: ${org.updatedAt ? new Date(org.updatedAt).toLocaleDateString() : 'N/A'}`}
                        />
                      </ListItem>
                    ))}
                    {selectedOrgsForSubmission.length === 0 && (
                      <ListItem>
                        <ListItemText primary="No hay organizaciones seleccionadas." />
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
        {step === 2 && challengeMode === 'new' && (
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt:1 }}>
            <Box sx={{ mb: 1 }}>
              <strong>Organizaciones seleccionadas:</strong>
              <ul>
                {selectedOrgsForSubmission.map(org => (
                  <li key={org.id}>{org.name} ({org.idNumber}) - {org.owner?.name || 'N/A'} | Creado: {org.createdAt ? new Date(org.createdAt).toLocaleDateString() : 'N/A'} | Modificado: {org.updatedAt ? new Date(org.updatedAt).toLocaleDateString() : 'N/A'}</li>
                ))}
              </ul>
            </Box>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'El nombre es obligatorio' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Nombre del reto" error={!!fieldState.error} helperText={fieldState.error?.message} fullWidth />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Descripción" multiline rows={2} fullWidth />
              )}
            />
            <Controller
              name="programVersions"
              control={control}
              rules={{ required: 'Selecciona al menos una versión de programa' }}
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  options={programVersions || []}
                  getOptionLabel={(option) => `${option.programId} - ${option.name}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  loading={pvLoading}
                  value={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Versiones de programa" error={!!fieldState.error} helperText={fieldState.error?.message} />
                  )}
                />
              )}
            />
            <Controller
              name="goal"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={goals || []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  loading={goalsLoading}
                  value={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Meta (opcional)" />
                  )}
                  noOptionsText={
                    showGoalInput ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <TextField size="small" label="Nueva meta" value={newGoalName} onChange={e => setNewGoalName(e.target.value)} />
                        <TextField size="small" label="Descripción" value={newGoalDesc} onChange={e => setNewGoalDesc(e.target.value)} />
                        <Button size="small" onClick={handleCreateGoal} disabled={createGoalMutation.status === 'pending' || !newGoalName}>
                          {createGoalMutation.status === 'pending' ? <CircularProgress size={16} /> : 'Crear meta'}
                        </Button>
                      </Box>
                    ) : (
                      <Button size="small" onClick={() => setShowGoalInput(true)}>Crear nueva meta</Button>
                    )
                  }
                />
              )}
            />
          </Box>
        )}
        {step === 2 && challengeMode === 'existing' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info">
              Se agregarán <strong>{selectedOrgsForSubmission.length}</strong> organización(es) al reto seleccionado.
              Las organizaciones que ya tengan un reto con el mismo nombre serán omitidas.
            </Alert>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Organizaciones seleccionadas:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {selectedOrgsForSubmission.map(org => (
                  <Typography key={org.id} variant="body2" component="span">
                    {org.name} ({org.idNumber});
                  </Typography>
                ))}
              </Box>
            </Box>
            <Autocomplete
              options={distinctExistingChallenges}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={selectedExistingChallenge}
              onChange={(_, value) => setSelectedExistingChallenge(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecciona un reto existente"
                  placeholder="Busca por nombre del reto"
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    {option.description && (
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              noOptionsText="No hay retos existentes disponibles"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleModalCloseAndReset}>Cancelar</Button>
        {step === 1 && (
          <Button variant="contained" onClick={handleNextStep} disabled={selectedOrgsForSubmission.length === 0 || orgListLoading || selectingAllMatching}>
            Siguiente
          </Button>
        )}
        {step === 2 && challengeMode === 'new' && (
          <>
            <Button onClick={handlePreviousStep}>Atrás</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={createChallengeMutation.status === 'pending'}
            >
              {createChallengeMutation.status === 'pending' ? <CircularProgress size={20} /> : 'Crear'}
            </Button>
          </>
        )}
        {step === 2 && challengeMode === 'existing' && (
          <>
            <Button onClick={handlePreviousStep}>Atrás</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddOrgsToExistingChallenge}
              disabled={!selectedExistingChallenge || addOrgsMutation.status === 'pending'}
            >
              {addOrgsMutation.status === 'pending' ? <CircularProgress size={20} /> : 'Agregar Organizaciones'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateChallengeModal; 